/*
 * Copyright (c) 2022 Puratos. All rights reserved.
 */

import {GOOGLE_AREA_LEVELS} from "../googleAreaLevels";
import {getLanguageIso} from "../../selectors/generic";

export let GooglePlaceService, GoogleAutocompleteService;

const initialiseGoogleServices = () => {
    if ((!GooglePlaceService || !GoogleAutocompleteService) && window.google?.maps?.places) {
        GooglePlaceService =  new window.google.maps.places.PlacesService(document.createElement("div"));
        GoogleAutocompleteService =  new window.google.maps.places.AutocompleteService();
    }
}

export const GOOGLE_PLACE_TYPES = {
    REGIONS: "(regions)",
    ADDRESSES: "address",
    CITIES: "(cities)",
};

export const googleAPIKey = window.jQuery("script[data-api-key]").attr("data-api-key");

export const fetchDataCallback = (sessionToken, setOptions, setLoading, country, apiKey = null) => (query) => {
    setLoading && setLoading(true);

    getPredictions(sessionToken, country, GOOGLE_PLACE_TYPES.ADDRESSES, query)
        .then(predictions => {
            setOptions(predictions.filter(e => !!e));
        }).catch((status) => {
            if (status === "ZERO_RESULTS") {
                return getPredictions(sessionToken, country, GOOGLE_PLACE_TYPES.CITIES, query)
            } else {
                setOptions([]);
            }
    }).then(predictions => {
        predictions && setOptions(predictions.filter(e => !!e));
    }).catch(status => {
        if (query && query.length > 15 && status === "ZERO_RESULTS") {
            return window.jQuery.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURI(query)}&key=${apiKey || googleAPIKey}&components=country:${country}&language=${getLanguageIso()}`);
        } else {
            setOptions([]);
        }
    }).then(d => {
        d && setOptions(d.status === "OK" && d.results && d.results.length > 0 ? [{
            description: d.results[0].formatted_address,
            place_id: d.results[0].place_id
        }] : []);
    }).finally(setLoading && setLoading(false));
};

export function getPlaceDetails(sessionToken, place, showPostalCode = false, googleAreaLevel, formatter) {
    const placeId = typeof place !== 'object' ? place : place.place_id;

    initialiseGoogleServices();
    return new Promise((resolve, reject) => {
        GooglePlaceService.getDetails({
            sessionToken: sessionToken,
            placeId,
            fields: [ "address_component" ]
        }, data => resolve(formatter(data, place, showPostalCode, googleAreaLevel)));
    });
}



export const formalizeGoogleStreetTypeahead = (data) => ({
    label: data.structured_formatting.main_text,
    id: data.place_id
});

export function extractAddressComponent(addressComponent, ...keys) {
    for (let key in keys) {
        const item = addressComponent.find(component => component.types.indexOf(keys[key]) > -1);

        if (item) {
            return item;
        }
    }
}

export const formalizeGoogleCityLocation = (prediction, place) => {
    if (prediction && prediction.address_components) {
        const country = extractAddressComponent(prediction.address_components, "country");
        const postalCode = extractAddressComponent(prediction.address_components, "postal_code");
        const city = extractAddressComponent(prediction.address_components, "locality", "administrative_area_level_1");
        const state = extractAddressComponent(prediction.address_components, "administrative_area_level_1");

        if (postalCode && city) {
            return {
                postalCode: postalCode ? postalCode.long_name : "",
                city: city ? city.long_name : "",
                state: state && state.short_name ? state.short_name : undefined,
                description: typeof place === 'object' ? place.description : place
            }
        }
    }
};

export const formalizeGoogleAddressLocation = (prediction, place, showPostalCode, googleAreaLevel) => {
    if (prediction && prediction.address_components) {
        const postalCode = extractAddressComponent(prediction.address_components, "postal_code");
        const street = extractAddressComponent(prediction.address_components, "route", "premise");
        const number = extractAddressComponent(prediction.address_components, "street_number");
        const city = extractAddressComponent(prediction.address_components, "locality", googleAreaLevel);
        const state = extractAddressComponent(prediction.address_components, googleAreaLevel) || extractAddressComponent(prediction.address_components, GOOGLE_AREA_LEVELS["Level 1"]);
        const country = extractAddressComponent(prediction.address_components, "country");
        if (city) {
            const postalCodeAndCity = showPostalCode ? (city ? city.long_name : "") + (state && state.short_name ? ", " + state.short_name : "") : (postalCode ? postalCode.long_name + ", " : "") + (city ? city.long_name : "") + (state && state.short_name ? ", " + state.short_name : "");
            return {
                postalCode: showPostalCode ? "" : (postalCode ? postalCode.long_name : ""),
                postalCodePresent: showPostalCode ? false : !!postalCode,
                city: city ? city.long_name : "",
                state: state && state.short_name ? state.short_name : undefined,
                stateLong: state && state.long_name ? state.long_name : undefined,
                street: street ? street.long_name : '',
                number: number ? number.long_name : '',
                postalCodeAndCity: postalCodeAndCity,
                description: typeof place === 'object' ? place.description : place,
                country: country ? country.short_name : ''
            }
        }
    }
};

export function getPredictions(sessionToken, country, type, search) {
    // Check if we are entering a number. If so, search for a region (postal code)
    if (type === GOOGLE_PLACE_TYPES.ADDRESSES && !isNaN(search)) {
        type = GOOGLE_PLACE_TYPES.REGIONS;
    }
    initialiseGoogleServices();
    return new Promise(((resolve, reject) => {
        GoogleAutocompleteService.getPlacePredictions({
            sessionToken: sessionToken,
            input: search,
            types: [ type ],
            componentRestrictions: { country },
        }, (predictions, status) => {
            if (status !== "OK") {
                reject(status);
            } else {
                resolve(predictions);
            }
        })
    }))
}
